import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useFeatures } from '../utils/features';
import MtnDetailModal from '../components/MtnDetailModal';
import { BeaconScreenPreview } from '../components/BeaconCard';
import FindProgress, { useBeaconFinds } from '../components/FindProgress';
// The list's reads, "where" and Find are shared with the phone Beacons screen (utils/beacons.js,
// docs/mobile-pwa-plan.md "One copy of every action").
import {
  beaconFetch, batteryLabel, chargeBadge, lastSeenLabel, screenStatus, normalizeBeaconId, currentActor, screenQr, firmwareNote,
  BEACON_LIST_REFRESH_MS, loadBeacons, beaconWhere, beaconCounts, findBeaconSaying, beaconState, assignBeaconByTrk,
  LOC_WAIT_MS, soPalletText, crateText,
} from '../utils/beacons';
import { salesOrderPalletPath, surplusRackCratePath } from '../utils/scanCodes';
import { desktopUi } from '../features/desktopUi';
import { FleetStatsStrip, BeaconHistoryPanel } from '../components/BeaconStats';
import { loadFleetStats, STATS_REFRESH_MS, daysLeftShort } from '../utils/beaconStats';

/** Materials-menu link to this page; hidden unless /features turns beacons on. */
export function BeaconsNavItem() {
  const { palletBeacons } = useFeatures();
  if (!palletBeacons.enabled) return null;
  return <NavDropdown.Item as={Link} to="/beacons">Pallet Beacons</NavDropdown.Item>;
}

function GatewaysCard() {
  const [gateways, setGateways] = useState([]);
  const [form, setForm] = useState({ gateway_id: '', name: '', station: '' });
  const [token, setToken] = useState(null); // {gateway_id, token}: shown once
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    const r = await beaconFetch('/beacon_gateways');
    if (r.ok && Array.isArray(r.body)) setGateways(r.body);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setMsg(null);
    const r = await beaconFetch('/beacon_gateways', { method: 'POST', body: JSON.stringify(form) });
    if (!r.ok) { setMsg(r.message); return; }
    setToken({ gateway_id: r.body.gateway_id, token: r.body.token });
    setForm({ gateway_id: '', name: '', station: '' });
    load();
  };

  const rotate = async (g) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`New token for ${g.gateway_id}? Its current token stops working at once, so the Pi needs the new one.`)) return;
    const r = await beaconFetch(`/beacon_gateways/${encodeURIComponent(g.gateway_id)}/rotate_token`, { method: 'POST' });
    if (!r.ok) { setMsg(r.message); return; }
    setToken({ gateway_id: r.body.gateway_id, token: r.body.token });
    load();
  };

  const toggle = async (g) => {
    const r = await beaconFetch(`/beacon_gateways/${encodeURIComponent(g.gateway_id)}`, {
      method: 'PATCH', body: JSON.stringify({ is_active: !g.is_active }),
    });
    if (!r.ok) setMsg(r.message);
    load();
  };

  return (
    <div className="card mb-3">
      <div className="card-header"><strong>Gateways</strong> <span className="text-muted small">(shop Pis that talk to the beacons)</span></div>
      <div className="card-body">
        {token && (
          <div className="alert alert-warning" data-testid="gateway-token">
            <div className="fw-bold">Token for {token.gateway_id}: copy it now, it is shown only once.</div>
            <div className="d-flex gap-2 align-items-center mt-1">
              <code className="user-select-all" style={{ wordBreak: 'break-all' }}>{token.token}</code>
              <button type="button" className="btn btn-sm btn-outline-dark"
                onClick={() => navigator.clipboard && navigator.clipboard.writeText(token.token)}>Copy</button>
            </div>
            <div className="small mt-1">Put it on the Pi in /etc/jobq/beacon-gateway.env as BEACON_GATEWAY_TOKEN.</div>
            <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setToken(null)}>I've saved it</button>
          </div>
        )}
        <table className="table table-sm align-middle">
          <thead><tr><th>Gateway</th><th>Name</th><th>Dock station</th><th>Last sync</th><th>Last report</th><th></th></tr></thead>
          <tbody>
            {gateways.length === 0 && <tr><td colSpan={6} className="text-muted small">No gateways yet.</td></tr>}
            {gateways.map((g) => (
              <tr key={g.gateway_id} className={g.is_active ? '' : 'text-muted'}>
                <td className="font-monospace">{g.gateway_id}</td>
                <td>{g.name}</td>
                <td>{g.station || <span className="text-muted">—</span>}</td>
                <td>{lastSeenLabel(g.last_seen_at)}</td>
                <td className="small">
                  {g.last_report ? `hears ${g.last_report.seen}${g.last_report.docked != null ? ` · ${g.last_report.docked} plugged in` : ''}` : '—'}
                </td>
                <td className="text-end">
                  <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => rotate(g)}>New token</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggle(g)}>
                    {g.is_active ? 'Switch off' : 'Switch on'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form className="row g-2 align-items-end" onSubmit={create}>
          <div className="col-auto">
            <label className="form-label small mb-0" htmlFor="gw-id">Gateway ID</label>
            <input id="gw-id" className="form-control form-control-sm" placeholder="laser-pi" value={form.gateway_id}
              onChange={(e) => setForm({ ...form, gateway_id: e.target.value })} />
          </div>
          <div className="col-auto">
            <label className="form-label small mb-0" htmlFor="gw-name">Name</label>
            <input id="gw-name" className="form-control form-control-sm" placeholder="Laser station Pi" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="col-auto">
            <label className="form-label small mb-0" htmlFor="gw-station">Dock station (optional)</label>
            <input id="gw-station" className="form-control form-control-sm" placeholder="Laser" value={form.station}
              onChange={(e) => setForm({ ...form, station: e.target.value })} />
          </div>
          <div className="col-auto">
            <button type="submit" className="btn btn-sm btn-primary" disabled={!form.gateway_id || !form.name}>Add gateway</button>
          </div>
        </form>
        {msg && <div className="small text-danger mt-2">{msg}</div>}
      </div>
    </div>
  );
}

/**
 * ?beacon=BCN-003 (an available beacon's screen QR: /s/bcn-3): that beacon on its own. A free one
 * gets an "Assign to a pallet" box (TRK number -> PUT /material_tracking/{id}/beacon, the same
 * move question as the TRK popup's dropdown); one on a pallet says which, with its TRK.
 */
function BeaconFocusCard({ beaconId, beacon, loading, onChanged, onOpenLot }) {
  const [lotText, setLotText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {tone, text}

  useEffect(() => { setLotText(''); setMsg(null); }, [beaconId]);

  const assign = async (e) => {
    e.preventDefault();
    setMsg(null);
    // The lot checks, the questions and the words: utils/beacons assignBeaconByTrk (the phone's too).
    setBusy(true);
    try {
      const said = await assignBeaconByTrk({ beaconId, lotText, ui: desktopUi, actor: currentActor() });
      if (said.cancelled) return;
      setMsg({ tone: said.tone, text: said.text });
      if (said.assigned) {
        setLotText('');
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const kind = beaconState(beacon);
  let body;
  if (kind === 'unregistered') {
    body = loading
      ? <div className="text-muted small">Loading {beaconId}…</div>
      : <div className="small">{beaconId} isn't registered yet. Switch it on near a gateway, or add it with Add above.</div>;
  } else if (kind === 'retired') {
    body = <div className="small">{beaconId} is out of service. Bring it back below to use it again.</div>;
  } else if (kind === 'on_pallet') {
    body = (
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <div data-testid="beacon-focus-lot">
          {beaconId} is on{' '}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => onOpenLot(beacon.lot.mtn_id)}>
            TRK {beacon.lot.trk}
          </button>
          {beacon.lot.material && <span className="text-muted small"> · {beacon.lot.material} {beacon.lot.size || ''}</span>}
        </div>
        <BeaconScreenPreview lines={beacon.screen.lines} qr={screenQr(beacon)} />
      </div>
    );
  } else if (kind === 'on_crate') {
    // A surplus crate (2026-11-03): the rack on that crate.
    body = (
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <div data-testid="beacon-focus-crate">
          {beaconId} is on{' '}
          <Link to={surplusRackCratePath(beacon.crate.crate_id)}>{crateText(beacon.crate)}</Link>
        </div>
        {beacon.screen && <BeaconScreenPreview lines={beacon.screen.lines} qr={screenQr(beacon)} />}
      </div>
    );
  } else if (kind === 'on_so_pallet') {
    // A sales-order pallet (2026-10-22): the order with that pallet in view.
    body = (
      <div className="d-flex flex-wrap gap-3 align-items-center">
        <div data-testid="beacon-focus-pallet">
          {beaconId} is on{' '}
          <Link to={salesOrderPalletPath(beacon.pallet.sales_order_id, beacon.pallet.pallet_id)}>{soPalletText(beacon.pallet)}</Link>
        </div>
        {beacon.screen && <BeaconScreenPreview lines={beacon.screen.lines} qr={screenQr(beacon)} />}
      </div>
    );
  } else {
    body = (
      <form className="d-flex flex-wrap gap-2 align-items-center" onSubmit={assign}>
        <label className="form-label mb-0" htmlFor="focus-lot">TRK number</label>
        {/* data-own-scans: a scanned pallet lot QR (/s/mtn-409) belongs here, not to ScanWedge. */}
        <input id="focus-lot" className="form-control form-control-sm" style={{ width: 170 }} placeholder="000409"
          inputMode="numeric" autoComplete="off" data-own-scans value={lotText} onChange={(e) => setLotText(e.target.value)} disabled={busy} />
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !lotText.trim()}>Assign</button>
        <span className="small text-muted">The number on the pallet's material label.</span>
      </form>
    );
  }

  const free = kind === 'free';
  return (
    <div className="card mb-3 border-primary" data-testid="beacon-focus">
      <div className="card-header">
        <strong>{free ? `Assign ${beaconId} to a pallet` : beaconId}</strong>
      </div>
      <div className="card-body">
        {body}
        {msg && <div className={`small mt-2 text-${msg.tone}`} role="status">{msg.text}</div>}
        {kind !== 'unregistered' && <BeaconHistoryPanel key={beaconId} beaconId={beaconId} beacon={beacon} />}
      </div>
    </div>
  );
}

/** /beacons: every pallet beacon, where it is, what it's on; register beacons; gateways. */
export default function BeaconsPage() {
  const { palletBeacons, loaded } = useFeatures();
  const location = useLocation();
  const focusId = normalizeBeaconId(new URLSearchParams(location.search).get('beacon'));
  const [beacons, setBeacons] = useState(null);
  const [error, setError] = useState(null);
  const [newId, setNewId] = useState('');
  const [msg, setMsg] = useState(null);
  const [openMtn, setOpenMtn] = useState(null);
  const finder = useBeaconFinds(beacons);
  const [stats, setStats] = useState(null);   // GET /beacons/stats: the fleet strip + days left per beacon

  const load = useCallback(async () => {
    const r = await loadBeacons();
    if (r.ok && r.body) { setBeacons(r.body.beacons); setError(null); } else setError(r.message);
  }, []);

  useEffect(() => {
    if (!palletBeacons.enabled) return undefined;
    load();
    const t = setInterval(load, BEACON_LIST_REFRESH_MS);
    return () => clearInterval(t);
  }, [palletBeacons.enabled, load]);

  useEffect(() => {
    if (!palletBeacons.enabled) return undefined;
    let cancelled = false;
    const read = () => loadFleetStats().then((r) => { if (!cancelled && r.ok && r.body) setStats(r.body); });
    read();
    // not while the tab is hidden; a read as soon as it is shown again
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') read(); }, STATS_REFRESH_MS);
    const onShow = () => { if (document.visibilityState === 'visible') read(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener('visibilitychange', onShow); };
  }, [palletBeacons.enabled]);

  if (!loaded) return <div className="container mt-4 text-muted">Loading…</div>;
  if (!palletBeacons.enabled) {
    return <div className="container mt-4"><div className="alert alert-secondary">Pallet beacons aren't switched on here yet.</div></div>;
  }

  const register = async (e) => {
    e.preventDefault();
    const id = normalizeBeaconId(newId);
    if (!id) { setMsg({ tone: 'danger', text: 'Use the number printed on the beacon, e.g. BCN-007 or 7.' }); return; }
    const r = await beaconFetch('/beacons', { method: 'POST', body: JSON.stringify({ beacon_id: id }) });
    if (!r.ok) { setMsg({ tone: 'danger', text: r.message }); return; }
    setMsg({ tone: 'success', text: r.body.created ? `${id} added.` : `${id} was already registered.` });
    setNewId('');
    load();
  };

  const find = async (b) => {
    const said = await findBeaconSaying(b.beacon_id, currentActor());
    if (said.find) {
      finder.start(b.beacon_id, said.find);       // the row follows it: queued -> sent -> lit
      setMsg(null);
    } else {
      setMsg({ tone: said.tone, text: said.text });
    }
    load();
  };

  const setRetired = async (b, retired) => {
    const r = await beaconFetch(`/beacons/${encodeURIComponent(b.beacon_id)}`, {
      method: 'PATCH', body: JSON.stringify({ retired, actor: currentActor() }),
    });
    if (!r.ok) setMsg({ tone: 'danger', text: r.message });
    load();
  };

  const list = beacons || [];
  const counts = beaconCounts(list);

  return (
    <div className="container-fluid mt-3" style={{ maxWidth: 1300 }}>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h3 className="mb-0"><i className="bi bi-broadcast me-2"></i>Pallet Beacons</h3>
        <span className="badge bg-secondary">{counts.total} beacons</span>
        <span className="badge bg-primary">{counts.onPallets} on pallets</span>
        <span className="badge bg-success">{counts.free} free</span>
        {counts.low > 0 && <span className="badge bg-danger">{counts.low} low battery</span>}
        {palletBeacons.location && (
          <Link to="/shop-map" className="btn btn-sm btn-outline-secondary ms-auto">
            <i className="bi bi-map me-1"></i>Shop map
          </Link>
        )}
        <form className={`d-flex gap-2 ${palletBeacons.location ? '' : 'ms-auto'}`.trim()} onSubmit={register}>
          <input className="form-control form-control-sm" style={{ width: 160 }} placeholder="Add beacon (BCN-007)"
            aria-label="Beacon to add" value={newId} onChange={(e) => setNewId(e.target.value)} />
          <button type="submit" className="btn btn-sm btn-outline-primary" disabled={!newId.trim()}>Add</button>
        </form>
      </div>
      {msg && <div className={`alert alert-${msg.tone} py-2`} role="status">{msg.text}</div>}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <FleetStatsStrip stats={stats} />

      {focusId && (
        <BeaconFocusCard
          beaconId={focusId}
          beacon={list.find((b) => b.beacon_id === focusId) || null}
          loading={beacons === null}
          onChanged={load}
          onOpenLot={setOpenMtn}
        />
      )}

      <div className="card mb-3">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr><th>Beacon</th><th>Pallet</th><th>Battery</th><th>Where</th><th>Screen</th><th></th></tr>
            </thead>
            <tbody>
              {beacons === null && <tr><td colSpan={6} className="text-muted">Loading…</td></tr>}
              {beacons && beacons.length === 0 && (
                <tr><td colSpan={6} className="text-muted">No beacons yet. Switch one on near a gateway, or add it by number.</td></tr>
              )}
              {list.map((b) => {
                const scr = screenStatus(b);
                const fwNote = firmwareNote(b);
                const rowClass = [b.state === 'retired' ? 'text-muted' : '', b.beacon_id === focusId ? 'table-primary' : ''].join(' ').trim();
                return (
                  <Fragment key={b.beacon_id}>
                    <tr className={rowClass}>
                      <td className="font-monospace fw-bold">
                        {b.beacon_id}
                        <Link to={`/beacons?beacon=${b.beacon_id}`} className="ms-1 small text-muted" title={`${b.beacon_id}: battery history and stats`}
                          aria-label={`${b.beacon_id} battery history and stats`}><i className="bi bi-graph-up" /></Link>
                        {fwNote && (
                          <div className={`small fw-normal text-${fwNote.tone}`} style={{ fontFamily: 'var(--bs-body-font-family)' }}
                            data-testid="firmware-note">{fwNote.text}</div>
                        )}
                      </td>
                      <td>
                        {b.lot ? (
                          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpenMtn(b.lot.mtn_id)}>
                            TRK {b.lot.trk}
                          </button>
                        ) : b.pallet ? (
                          <Link className="small" data-testid="beacon-row-pallet"
                            to={salesOrderPalletPath(b.pallet.sales_order_id, b.pallet.pallet_id)}>{soPalletText(b.pallet)}</Link>
                        ) : beaconState(b) === 'on_crate' ? (
                          <Link className="small" data-testid="beacon-row-crate"
                            to={surplusRackCratePath(b.crate.crate_id)}>{crateText(b.crate)}</Link>
                        ) : <span className="text-muted">{b.state === 'retired' ? 'retired' : 'free'}</span>}
                        {b.lot && <div className="small text-muted">{b.lot.material} {b.lot.size || ''}</div>}
                        {b.lot_done && <div className="small text-warning">lot done: frees on next check-in</div>}
                      </td>
                      <td>
                        <span className={chargeBadge(b) ? 'fw-bold' : ''}>{batteryLabel(b)}</span>
                        {b.charging ? <span className="badge bg-success ms-1">charging</span> : null}
                        {chargeBadge(b) && (
                          <span className={`badge ms-1 ${chargeBadge(b).className}`} data-testid="charge-badge">
                            {chargeBadge(b).text}
                          </span>
                        )}
                        {(() => {
                          const left = stats && stats.drain ? daysLeftShort(stats.drain[b.beacon_id]) : null;
                          return left ? <div className={`small text-${left.tone}`} data-testid="days-left">{left.text}</div> : null;
                        })()}
                      </td>
                      <td className="small">{beaconWhere(b)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ transform: 'scale(0.75)', transformOrigin: 'left center' }}>
                            <BeaconScreenPreview lines={b.screen.lines} qr={screenQr(b)} />
                          </div>
                          <span className={`small text-${scr.tone}`}>{scr.text}</span>
                        </div>
                      </td>
                      <td className="text-end text-nowrap">
                        <button type="button" className="btn btn-sm btn-success me-1" onClick={() => find(b)}
                          disabled={b.state === 'retired' || finder.busy(b.beacon_id)}>
                          {finder.busy(b.beacon_id) ? 'Finding…' : 'Find'}
                        </button>
                        {b.state === 'retired'
                          ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRetired(b, false)}>Bring back</button>
                          : <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRetired(b, true)}
                              disabled={b.state === 'assigned'} title={b.state === 'assigned' ? 'Release it from its pallet first' : ''}>Retire</button>}
                      </td>
                    </tr>
                    {finder.finds[b.beacon_id] && (
                      // the Find on a full-width row under its beacon: room for the shop map
                      <tr className={rowClass} data-testid={`find-row-${b.beacon_id}`}>
                        <td colSpan={6} className="pt-0" style={{ whiteSpace: 'normal' }}>
                          {/* the map goes once the find is over and its location had LOC_WAIT_MS to arrive */}
                          <FindProgress find={finder.finds[b.beacon_id]} onRetry={() => find(b)}
                            onChange={finder.track(b.beacon_id)} onSettled={load} clearMapAfterMs={LOC_WAIT_MS} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <GatewaysCard />

      <MtnDetailModal mtnId={openMtn} show={!!openMtn} onHide={() => { setOpenMtn(null); load(); }} onChanged={load} />
    </div>
  );
}
